import Image, { getImageProps } from "next/image";
import Heading from "~/componentsDS/Typography/Heading";
import { SymptomSlide } from "./data";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { CaretRightIcon } from "@phosphor-icons/react/ssr";
import { QuizCTA as QuizLink } from "~/components/features/quiz";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import Link from "next/link";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Overline from "~/componentsDS/Typography/Overline";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

export const TreatmentCard = ({ slide }: { slide: SymptomSlide }) => {
    const {
        bgImageUrl,
        title,
        description,
        mobileDescription,
        learnMoreHref,
        learnMoreAccessibleSuffix,
    } = slide;
    const desktopAccessibleSuffix = learnMoreAccessibleSuffix.replace(" about ", " for ");
    const {
        props: desktopImageProps,
    } = getImageProps({
        src: bgImageUrl.desktop,
        alt: "",
        fill: true,
        sizes: "(max-width: 1023px) calc(100vw - 48px), 850px",
        quality: 95,
    });

    const sendEvent = useTrackEvent();

    const handleLearnMoreClick = () => {
        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "hormonal_stage",
                meta_data: {
                    click_text: "learn more about Oestra®",
                    product_name: "oestra"
                },
            },
        });
    };

    return (
        <article className="relative h-[352px] overflow-hidden rounded-2xl text-pureWhite md:h-[562px]">
            <div
                className="absolute inset-0"
                aria-hidden="true"
            >
                <picture>
                    <source
                        media="(min-width: 768px)"
                        srcSet={desktopImageProps.srcSet}
                        sizes={desktopImageProps.sizes}
                    />
                    <Image
                        src={bgImageUrl.mobile}
                        alt=""
                        fill
                        sizes="calc(100vw - 48px)"
                        quality={95}
                        loading="lazy"
                        className="object-cover object-center"
                    />
                </picture>
            </div>

            <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col px-6 pb-5 lg:inset-y-0 lg:bottom-auto lg:p-8">
                <div className="mt-auto lg:mt-0">
                    <Heading as="h4" size="sm" className="max-md:font-featureDisplay max-md:text-[28px] max-md:leading-[1.2] max-md:tracking-[0.56px]">
                        {title}
                    </Heading>
                    <Paragraph size="lg" className="mt-2">
                        <span className="lg:hidden">{mobileDescription}</span>
                        <span className="hidden lg:inline">{description}</span>
                    </Paragraph>
                </div>

                <div className="flex items-center max-md:justify-between gap-2 md:gap-10 mt-5 lg:mt-6">
                    <QuizLink
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        variant="secondary"
                        size="md"
                        eventData={{
                            event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                            widget: "hormonal_stage",
                            meta_data: {
                                click_text: "Get started",
                                product_name: "oestra"
                            },
                        }}
                        className="max-sm:px-5"
                    >
                        Get started
                    </QuizLink>

                    <Link
                        href={learnMoreHref}
                        onClick={handleLearnMoreClick}
                        className="group inline-flex items-center gap-3 py-4 transition-colors hover:text-primary200"
                    >
                        <Overline as="span" className="inline-block whitespace-nowrap pt-[2px] underline decoration-transparent underline-offset-4 transition-colors duration-300 group-hover:decoration-current">
                            <TextWithRegisteredMark text="learn more about oestra®" className="hidden md:block" />
                            <span className="md:hidden">learn more</span>
                            <span className="sr-only md:hidden">{learnMoreAccessibleSuffix}</span>
                            <span className="sr-only hidden md:inline">{desktopAccessibleSuffix}</span>
                        </Overline>
                        <CaretRightIcon size={16} />
                    </Link>
                </div>
            </div>
        </article>
    );
}
